/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { PureComponent, useMemo } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import {
    Alert,
    Button,
    Cell,
    FlexCol,
    FlexRow,
    Heading,
    HtmlParser,
    Icon,
    Inline,
    Notification,
    NotificationsWrapper,
    Row,
    StickyBottomHelper,
    StyledModal,
    Text,
    VerticalDivider,
    useFlags,
} from 'sarsaparilla';
import { FavoriteHeart } from 'shared-ui/favorites';
import { getGatewayUrl } from 'shared-ui/utils/getGatewayUrl';
import { LoadingWrapper } from 'ui-lottery';
import { FeatureFlagArray } from './featureFlag/FeatureFlagArray';
import { FacilityProps, PassTypeProps, ReservationProps } from '../props';
import { formatPrice } from '../utils';
import { passSelectionBySite } from '../../dev/routesArray';
import NeedToKnow from './NeedToKnow';
import LEGACY_Overview from './LEGACY_Overview';
import PageRegion from './layout/PageRegion';
import PassBottomNav from './PassBottomNav';
import PassInfoForm from './PassInfoForm';
import PassRatesTable from './PassRatesTable';
import SitePassSelectionHeadingAndFees from './SitePassSelectionHeadingAndFees';
import LEGACY_SitePassBanner from './LEGACY_SitePassBanner';
import PassSelectionError from '../containers/PassSelectionError';
import LEGACY_PassTypesTable from './LEGACY_PassTypesTable';
import RenderIf from '../utils/JSXUtils';
import LEGACY_ReservationSummary from './LEGACY_ReservationSummary';
import LoadingSkeleton from './loadingSkeleton/LoadingSkeleton';
import PassImage from './PassImage';
import {
    showSitePassAlert as showSitePassAlertUtil,
    showSitePassBanner as showSitePassBannerUtil,
} from '../utils/launchDarklyUtils';
import { INTERAGENCY_PASSES, SITE_PASS_BANNERS } from '../constants';

function LEGACY_PassSelection(props) {
    const { facilitiesUsingSitePassIntegration, enablePassRatesModal } = useFlags();

    const shouldShowSitePassAlert = useMemo(() => {
        return showSitePassAlertUtil(
            props.facilityId,
            facilitiesUsingSitePassIntegration
        );
    }, [facilitiesUsingSitePassIntegration, props.facilityId]);

    const shouldShowSitePassesTable = useMemo(() => {
        const shouldShowSitePassBanner = showSitePassBannerUtil(
            props.facilityId,
            facilitiesUsingSitePassIntegration
        );
        const sitePassBanner = SITE_PASS_BANNERS[props?.facilityId];
        return shouldShowSitePassBanner && !sitePassBanner?.showPassesByDefault;
    }, [facilitiesUsingSitePassIntegration, props.facilityId]);

    return (
        <LEGACY_PassSelectionComp
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            showSitePassAlert={shouldShowSitePassAlert}
            showSitePassesTable={shouldShowSitePassesTable}
            enablePassRatesModal={enablePassRatesModal}
        />
    );
}

LEGACY_PassSelection.propTypes = {
    facilityId: PropTypes.string,
    enablePassRatesModal: PropTypes.bool,
};

export default LEGACY_PassSelection;

class LEGACY_PassSelectionComp extends PureComponent {
    static defaultProps = {
        loading: {},
        facility: {},
        passCounts: {},
        passTypes: [],
    };

    static propTypes = {
        children: PropTypes.node,
        facility: FacilityProps,
        facilityId: PropTypes.string,
        handleNeedToKnowChecked: PropTypes.func.isRequired,
        loaded: PropTypes.shape({
            facilityByIdOrSlug: PropTypes.bool,
            reservations: PropTypes.bool,
            passTypes: PropTypes.bool,
        }),
        loading: PropTypes.shape({
            facilities: PropTypes.bool,
            passTypes: PropTypes.bool,
        }),
        maxTotalGuestsReached: PropTypes.bool,
        needToKnowChecked: PropTypes.bool,
        onPassCountChange: PropTypes.func.isRequired,
        onRemoveReservation: PropTypes.func.isRequired,
        onReservationChange: PropTypes.func.isRequired,
        passCounts: PropTypes.object,
        passTypes: PropTypes.arrayOf(PassTypeProps),
        passTypeId: PropTypes.string,
        passTypesRaw: PropTypes.arrayOf(PassTypeProps),
        proceedButton: PropTypes.node.isRequired,
        reservationId: PropTypes.string,
        reservations: PropTypes.arrayOf(ReservationProps),
        submitAttempted: PropTypes.bool,
        parentAsset: PropTypes.array,
        isCsrAccount: PropTypes.bool,
        showSitePassAlert: PropTypes.bool,
        showSitePassesTable: PropTypes.bool,
        enablePassRatesModal: PropTypes.bool,
    };

    state = {
        isSummaryModalOpen: false,
        showPassesTable: false,
        interagencyPassModalStatus: 'IDLE',
    };

    componentDidMount() {
        this.setState((prevState) => ({
            ...prevState,
            showPassesTable: !this.props?.showSitePassesTable,
        }));
    }

    componentDidUpdate(prevProps) {
        if (prevProps.showSitePassesTable !== this.props.showSitePassesTable) {
            this.setState((prevState) => ({
                ...prevState,
                showPassesTable: !this.props.showSitePassesTable,
            }));
        }
    }

    handlePassInfoChange = (reservationId, passTypeId, passInfo, isValid) => {
        const reservation = this.props.reservations.find(
            ({ id }) => id === reservationId
        );
        if (reservation) {
            // Default "discount" feature.
            const discount = {};

            // Format "accepted" feature.
            const passType = this.props.passTypes.find(
                ({ id }) => id === reservation.pass_type_id
            );
            const accepted = {};
            if (passType && passType.features.accepted) {
                accepted.accepted = passType.features.accepted.reduce(
                    (memo, key) => ({
                        ...memo,
                        [key]: true,
                    }),
                    {}
                );
            }

            // Build updated reservation.
            const updatedReservation = {
                ...reservation,
                pass_info: passInfo,

                // New API updates:
                pass_type_id: passTypeId || reservation.pass_type_id,
                holder_info: passInfo.holder_info,
                features: {
                    discount,
                    ...passInfo.features,
                    ...accepted,
                },
            };

            this.props.onReservationChange(updatedReservation, isValid);
        }
    };

    toggleSummaryModal = () =>
        this.setState((prevState) => ({
            isSummaryModalOpen: !prevState.isSummaryModalOpen,
        }));

    scrollToPassTypesTable = () => {
        setTimeout(() => {
            const offset = 100; // This prevents the table from being hidden by the header
            const tableElement = document?.getElementsByClassName(
                'parkpass-pass-types-table-wrapper'
            )?.[0];
            const tableElementOffsetTop = tableElement?.offsetTop ?? 0;
            const tableElementOffsetParentTop =
                tableElement?.offsetParent?.offsetTop ?? 0;
            const top = tableElementOffsetTop + tableElementOffsetParentTop - offset;
            if (tableElement) {
                window.scrollTo({ top, behavior: 'smooth' });
            }
        }, 0);
    };

    handleToggleShowPassesTable = () => {
        this.setState(
            (prevState) => ({
                ...prevState,
                showPassesTable: true,
            }),
            this.scrollToPassTypesTable
        );
    };

    handleToggleInteragencyPassModal =
        (status = 'IDLE') =>
        () => {
            this.setState((prevState) => ({
                ...prevState,
                interagencyPassModalStatus: status,
            }));
        };

    handleGoToInterAgencyPassPage = () => {
        this.handleToggleInteragencyPassModal()();
        window.location.href = '/pass';
    };

    renderInterAgencyPassListItems = () => {
        return INTERAGENCY_PASSES?.map((interAgencyPass) => (
            <li
                key={interAgencyPass}
                className="parkpass-order-details__interagency_pass_list-item"
            >
                <PassImage aria-hidden="true" type={interAgencyPass} />
            </li>
        ));
    };

    render() {
        const {
            children,
            facility,
            facilityId,
            parentAsset,
            loaded,
            loading,
            maxTotalGuestsReached,
            needToKnowChecked,
            isCsrAccount,
            passCounts,
            passTypes,
            passTypesRaw,
            proceedButton,
            reservationId,
            reservations,
            submitAttempted,
            showSitePassAlert,
            enablePassRatesModal,
        } = this.props;

        if (!facilityId) {
            return null;
        }

        let pricing = null;
        if (facility.id && Array.isArray(get(facility, 'contents.pricing'))) {
            const pricingContent = facility.contents.pricing.flatMap(
                ({ paragraphs = [] }, p) =>
                    paragraphs.map((html, n) => (
                        <p key={`html-${p}-${n}`}>
                            <HtmlParser html={html} />
                        </p>
                    ))
            );

            pricing = (
                <NotificationsWrapper noticeType="info" closeButton={false}>
                    {pricingContent}
                </NotificationsWrapper>
            );
        }

        const { isSummaryModalOpen } = this.state;
        const reservationSummary = (
            <LEGACY_ReservationSummary
                passTypes={passTypes}
                reservations={reservations}
                facility={facility}
            >
                {children}
            </LEGACY_ReservationSummary>
        );
        const recarea = parentAsset?.find((item) => item.entity_id === facility.asset_id);

        if (!loaded?.facilityByIdOrSlug || !loaded.passTypes) {
            return <LoadingSkeleton />;
        }

        const locationChain = [];
        if (!isNil(recarea)) {
            locationChain.push(
                <span key="partOf">
                    Part of{' '}
                    <a
                        href={getGatewayUrl(facility.asset_id)}
                        rel="noreferrer"
                        target="_blank"
                    >
                        {recarea?.name}
                    </a>
                </span>
            );
        }
        if (locationChain.length) {
            locationChain.push(<VerticalDivider key="mobile-favorite-divider" />);
        }
        locationChain.push(
            <FavoriteHeart
                entityId={facility.asset_id}
                entityType={'sitepass'}
                showFavoriteText
                key="favorite-button"
            />
        );

        const subheader = (
            <div className="location-chain mt-1 mb-1">
                <Inline space={['xs', 'sm', 'md']}>{locationChain}</Inline>
            </div>
        );

        return (
            <div className="parkpass-order-details">
                <div className="rec-nested-wrap">
                    <div id="page-body" className="rec-parkpass-left-rail">
                        <Heading headingLevel={1} appearance="title" translate="no">
                            Site Pass Selection
                        </Heading>

                        <Text color="gray" size="lg">
                            {facility.name}
                        </Text>

                        {subheader}

                        <PassSelectionError
                            reservations={reservations}
                            submitAttempted={submitAttempted}
                            needToKnowChecked={needToKnowChecked}
                        />

                        {RenderIf(
                            loading.passTypes || (loaded.passTypes && !reservationId)
                        )(
                            <LEGACY_SitePassBanner
                                facilityId={facilityId}
                                showPasses={this.state?.showPassesTable}
                                onToggleShowPasses={this.handleToggleShowPassesTable}
                            />
                        )}
                        {RenderIf(isCsrAccount)(
                            <div className="mb-4">
                                <NotificationsWrapper
                                    noticeType="error"
                                    closeButton={false}
                                >
                                    <div className="rec-notification-title">
                                        Error Notification:
                                    </div>
                                    <p>
                                        Please select a customer to continue with the
                                        reservation.
                                    </p>
                                </NotificationsWrapper>
                            </div>
                        )}
                        {RenderIf(facility?.contents?.overview)(() => (
                            <LEGACY_Overview
                                overview={get(facility, 'contents.overview', [])}
                            />
                        ))}

                        {RenderIf(
                            loading.passTypes || (loaded.passTypes && !reservationId)
                        )(
                            <LoadingWrapper loading={loading.passTypes} size="lg">
                                <FeatureFlagArray
                                    flag="disableNpsParkpasses"
                                    skeleton
                                    flagValueToMatch={facility?.id}
                                    fallback={
                                        <PageRegion>
                                            {enablePassRatesModal ? (
                                                <SitePassSelectionHeadingAndFees
                                                    facilityName={facility?.name}
                                                    passTypes={passTypes}
                                                />
                                            ) : (
                                                <Row>
                                                    <Cell>
                                                        <PassRatesTable
                                                            passTypes={passTypes}
                                                        />
                                                    </Cell>
                                                </Row>
                                            )}
                                            <Row>
                                                {showSitePassAlert && (
                                                    <Cell>
                                                        <Alert
                                                            className="mb-2"
                                                            type="warning"
                                                        >
                                                            <Text
                                                                className="mr-1"
                                                                tag="span"
                                                            >
                                                                If you already have an
                                                                Interagency Pass, you
                                                                don&apos;t need a Site
                                                                Pass to enter this
                                                                facility.
                                                            </Text>
                                                            <Button
                                                                appearance="link"
                                                                iconAfterElement={
                                                                    <Icon iconName="arrow-forward" />
                                                                }
                                                                onClick={this.handleToggleInteragencyPassModal(
                                                                    'OPEN'
                                                                )}
                                                            >
                                                                Learn More
                                                            </Button>
                                                        </Alert>
                                                    </Cell>
                                                )}
                                                <Cell>
                                                    {reservationId && (
                                                        <h4 className="parkpass-section-heading">
                                                            Modify
                                                        </h4>
                                                    )}

                                                    {this.state?.showPassesTable && (
                                                        <>
                                                            <p>
                                                                Enter the number of passes
                                                                you would like to purchase
                                                                from the available choices
                                                                below:
                                                            </p>
                                                            <LEGACY_PassTypesTable
                                                                maxTotalGuestsReached={
                                                                    maxTotalGuestsReached
                                                                }
                                                                reservations={
                                                                    reservations
                                                                }
                                                                passCounts={passCounts}
                                                                passTypeId={
                                                                    this.props.passTypeId
                                                                }
                                                                passTypes={passTypes}
                                                                passTypesRaw={
                                                                    passTypesRaw
                                                                }
                                                                pricing={pricing}
                                                                onChange={
                                                                    this.props
                                                                        .onPassCountChange
                                                                }
                                                                facilityId={facilityId}
                                                            />
                                                        </>
                                                    )}
                                                </Cell>
                                            </Row>
                                        </PageRegion>
                                    }
                                >
                                    <PageRegion>
                                        <Notification
                                            type="warning"
                                            title="Coronavirus (COVID-19) Update"
                                            headingLevel={3}
                                            className="sitepass-covid19-notification"
                                            id="sitepass-corona-virus-notification"
                                        >
                                            <p>
                                                Please be aware that sales are currently
                                                suspended for the Site Pass at this
                                                location due to impacts from the
                                                coronavirus (COVID-19). We will provide
                                                updates as the situation evolves. We
                                                appreciate your patience and
                                                understanding.
                                            </p>
                                        </Notification>
                                    </PageRegion>
                                </FeatureFlagArray>
                            </LoadingWrapper>
                        )}

                        {RenderIf(reservations.length)(() => (
                            <div>
                                <h3 className="parkpass-section-heading h4">
                                    {reservationId ? 'Modify' : 'Pass Details'}
                                </h3>
                                <p>
                                    {reservationId
                                        ? 'Please modify the pass information below:'
                                        : 'Please provide the required information below:'}
                                </p>
                                {reservations.map((reservation, number) => {
                                    const passType = passTypes.find(
                                        ({ id }) => id === reservation.pass_type_id
                                    );
                                    if (!passType) {
                                        return null;
                                    }
                                    return (
                                        <Row key={`${reservation.id}`}>
                                            <Cell>
                                                <PassInfoForm
                                                    submitAttempted={submitAttempted}
                                                    facility={facility}
                                                    number={number + 1}
                                                    passInfo={reservation.pass_info}
                                                    passType={passType}
                                                    price={formatPrice(reservation.price)}
                                                    renderRemove
                                                    isPending={reservation.is_pending}
                                                    reservationId={reservation.id}
                                                    onChange={this.handlePassInfoChange.bind(
                                                        this,
                                                        reservation.id
                                                    )}
                                                    onRemove={
                                                        this.props.onRemoveReservation
                                                    }
                                                />
                                            </Cell>
                                        </Row>
                                    );
                                })}
                            </div>
                        ))}

                        {RenderIf(reservationId)(() => (
                            <Button
                                appearance="secondary"
                                size="sm"
                                data-testid="parkpass-add-another-pass"
                                className="parkpass-add-another-pass"
                                onClick={() => {
                                    window.location = passSelectionBySite(facility.id);
                                    return null;
                                }}
                                iconBeforeElement={<Icon iconName="add-circle" />}
                            >
                                ADD ANOTHER PASS
                            </Button>
                        ))}

                        {RenderIf(facility.id)(() => (
                            <div className="parkpass-info-box">
                                <NeedToKnow
                                    needToKnow={get(
                                        facility,
                                        'contents.need_to_know',
                                        []
                                    )}
                                    className={
                                        submitAttempted && !needToKnowChecked
                                            ? 'rec-error'
                                            : ''
                                    }
                                    siteName={facility.name}
                                    noFeePeriods={facility.free_days}
                                    checked={needToKnowChecked}
                                    onChange={this.props.handleNeedToKnowChecked}
                                />
                            </div>
                        ))}

                        {facility.id && !!reservations.length && (
                            <StickyBottomHelper scrollingContainerId="page-body">
                                <div className="rec-order-detail-page-actions">
                                    <FlexRow hasGutters={false}>
                                        <FlexCol xs={6} md={8} alignSelf="center">
                                            <button
                                                onClick={() => this.toggleSummaryModal()}
                                                className="rec-button-link display-sm-block"
                                                type="button"
                                            >
                                                {'Show Summary'}
                                            </button>
                                        </FlexCol>
                                        <FlexCol xs={6} md={4}>
                                            <PassBottomNav
                                                proceedButton={proceedButton}
                                            />
                                        </FlexCol>
                                    </FlexRow>
                                </div>
                            </StickyBottomHelper>
                        )}

                        <StyledModal
                            isOpen={isSummaryModalOpen}
                            onRequestClose={this.toggleSummaryModal}
                            role="complementary"
                            contentLabel="Order Summary"
                            heading="Summary"
                            size="md"
                            className="rec-modal-order-summary"
                        >
                            <div className="rec-modal-content-wrap">
                                {reservationSummary}
                            </div>
                        </StyledModal>

                        <StyledModal
                            isOpen={this.state?.interagencyPassModalStatus === 'OPEN'}
                            size="md"
                            onRequestClose={this.handleToggleInteragencyPassModal()}
                            heading="Do you have a physical Interagency Pass?"
                        >
                            <div>
                                <Text className="mb-3">
                                    If you already have one, you don&apos;t need a digital
                                    Site Pass to enter this facility. Be sure your
                                    Interagency Pass is valid for your trip dates and
                                    bring it with you -- you will be asked to show the
                                    pass once you arrive.
                                </Text>
                                <Heading
                                    className="mb-2"
                                    headingLevel={6}
                                    appearance="h6"
                                >
                                    Valid Interagency Passes:
                                </Heading>
                                <ul className="parkpass-order-details__interagency_pass_list">
                                    {this.renderInterAgencyPassListItems()}
                                </ul>
                                <Button
                                    className="mt-3"
                                    appearance="link"
                                    iconAfterElement={<Icon iconName="arrow-forward" />}
                                    onClick={this.handleGoToInterAgencyPassPage}
                                >
                                    Learn More About Interagency Passes
                                </Button>
                            </div>
                        </StyledModal>
                    </div>

                    <div className="rec-parkpass-right-rail">
                        {facility.id && reservationSummary}
                    </div>
                </div>
            </div>
        );
    }
}

// cspell:ignore parkpass, noreferrer, recarea, sitepass, Parkpasses, apos, testid
